import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import {
  Trophy,
  AlertCircle,
  Edit,
  Trash2,
  UserPlus,
  Calendar as CalendarIcon,
  ListChecks,
  BarChart3,
  Users,
  ArrowLeft,
  Clock,
  ChevronLeft,
  ChevronRight,
  Plus,
  MoreHorizontal,
  Share2,
} from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import FormTorneo from './FormTorneo';
import InscribirGrupoModal from './InscribirGrupoModal';
import EstadisticasTorneo from './EstadisticasTorneo';
import { useOrganization } from '../../contexts/OrganizationContext';
import { esTorneoExterno, puedeEditarJuego } from '../../lib/torneoOrigen';
import CompartirRolModal from './CompartirRolModal';
import JuegoFila from './JuegoFila';
import { tituloDelRolInterno } from '../../lib/rolInterno';
import { jornadasDeTorneo, jornadaActual, bloquesPorHora } from '../../lib/jornadasInterno';
import { armarRolInterno } from '../../rol/mapeoRolInterno';
import { proximoJuego } from '../../lib/proximoJuego';
import { filasDeRecord, hayRecord } from '../../lib/recordInterno';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  Alert,
  AlertTitle,
  AlertDescription,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from '../ui';
import {
  getTorneoByIdWithInscripciones,
  updateTorneo,
  deleteTorneo,
  deleteInscripcion,
  getJuegosByTorneo,
} from '../../lib/supabase';
import { parseLocalDate } from '../../lib/dateUtils';
import { cn, fixMojibake } from '../../lib/utils';
import {
  FORMATO_LABELS,
  isCerrado,
  ETAPA_LABELS,
  ETAPA_STYLES,
  etapaDeTorneo,
  INSCRIPCION_STATUS_STYLES,
  INSCRIPCION_STATUS_LABELS
} from './torneoStatus';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';

const TAB_CONFIG = [
  { id: 'inscripciones', label: 'Inscripciones', icon: ListChecks },
  // Se llama Rol, no Calendario: es el rol del torneo que el club arma y comparte.
  { id: 'calendario', label: 'Rol', icon: CalendarIcon },
  // Era "Posiciones": prometia la tabla de la liga, y aqui solo esta el record de los
  // grupos del club (el club captura solo sus juegos). Ver lib/recordInterno.js.
  { id: 'posiciones', label: 'Récord', icon: BarChart3 },
  { id: 'estadisticas', label: 'Estadísticas', icon: Trophy }
];

const formatDateRange = (inicio, fin) => {
  if (!inicio && !fin) return 'Sin fecha';
  try {
    if (inicio && fin) {
      return `${format(parseLocalDate(inicio) || new Date(), 'dd MMM', { locale: es })} - ${format(parseLocalDate(fin) || new Date(), 'dd MMM yyyy', { locale: es })}`;
    }
    return format(parseLocalDate(inicio || fin) || new Date(), 'dd MMM yyyy', { locale: es });
  } catch {
    return 'Fecha no disponible';
  }
};

const TorneoDetail = () => {
  const { torneoId } = useParams();
  const navigate = useNavigate();
  const { canManageTorneos } = usePermissions();
  const { org } = useOrganization();
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();

  const [torneo, setTorneo] = useState(null);
  const [juegos, setJuegos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Abre en CALENDARIO, no en Inscripciones. Se entra a ver cuando se juega; inscribir
  // es de administracion y se hace una vez al inicio, no cada semana.
  const [activeTab, setActiveTab] = useState('calendario');
  const [showForm, setShowForm] = useState(false);
  const [showInscribir, setShowInscribir] = useState(false);
  const [saving, setSaving] = useState(false);
  const [grupoEstadisticas, setGrupoEstadisticas] = useState('all');
  // El rol que se comparte es el de UN dia: es lo que un papa pide ("el del sabado"),
  // y es la unidad que el flyer sabe dibujar (una rejilla de horas por campos).
  const [rolACompartir, setRolACompartir] = useState(null);
  // La jornada abierta en la pestana Rol, por su clave (la fecha) y no por posicion: si
  // al recargar aparece un dia nuevo antes, se sigue viendo el mismo. null = la de hoy.
  const [jornadaKey, setJornadaKey] = useState(null);

  // La pantalla de "Cargando..." solo en la PRIMERA carga. Cada accion (inscribir,
  // editar, quitar, guardar) recargaba los datos con loading=true y la pantalla
  // entera se desmontaba: se perdian scroll y foco, se sentia como una recarga de la
  // pagina (el dueno, 2026-09-07). Las recargas siguientes son silenciosas.
  const cargadoRef = useRef(false);
  const loadAll = useCallback(async () => {
    if (!torneoId) return;
    if (!cargadoRef.current) setLoading(true);
    setError(null);
    try {
      const [torneoRes, juegosData] = await Promise.all([
        getTorneoByIdWithInscripciones(torneoId),
        getJuegosByTorneo(torneoId).catch(() => [])
      ]);
      if (torneoRes.error) throw torneoRes.error;
      setTorneo(torneoRes.data);
      setJuegos(Array.isArray(juegosData) ? juegosData : []);
    } catch (err) {
      console.error('Error cargando torneo:', err);
      setError('No pudimos cargar la información del torneo.');
    } finally {
      setLoading(false);
      cargadoRef.current = true;
    }
  }, [torneoId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const inscripciones = useMemo(() => {
    return (torneo?.torneos_inscripciones || []).filter((i) => i.activo !== false);
  }, [torneo]);

  const record = useMemo(() => filasDeRecord(inscripciones), [inscripciones]);

  // La pregunta que mas se hace, contestada donde se hace. El dato ya estaba cargado.
  const proximo = useMemo(() => proximoJuego(juegos), [juegos]);

  // El torneo partido en jornadas (un dia = una jornada); la pestana Rol muestra UNA.
  // Antes pintaba todas, una debajo de otra: con 15 jornadas de 10 juegos, tres
  // pantallas de tarjetas para encontrar la del sabado (el dueno, 2026-09-09).
  const jornadas = useMemo(() => jornadasDeTorneo(juegos), [juegos]);
  const jornadaIdx = useMemo(() => {
    const elegida = jornadaKey ? jornadas.findIndex((j) => j.key === jornadaKey) : -1;
    return elegida >= 0 ? elegida : jornadaActual(jornadas);
  }, [jornadas, jornadaKey]);

  // La MISMA palabra que la tarjeta de la lista: la etapa. Antes el encabezado mostraba
  // `estatus` -- el espejo grueso -- asi que un torneo aparecia como "Borrador" en la
  // lista y como "Proximo" al abrirlo. Dos nombres para el mismo hecho.
  const etapaKey = etapaDeTorneo(torneo);
  const statusLabel = ETAPA_LABELS[etapaKey] || etapaKey;
  const torneoCerrado = isCerrado(torneo);
  // Esta pantalla es la del ORGANIZADOR: inscribir, programar, poner marcadores. Si el
  // torneo es de otra organizacion, el club no hace nada de eso -- su lugar es la
  // participacion. Antes vivian juntas y la pantalla preguntaba 16 veces quien eras.
  const esAjeno = esTorneoExterno(torneo, org?.id);

  const handleSubmitTorneo = async (torneoData) => {
    if (!torneo) return;
    try {
      setSaving(true);
      const { error } = await updateTorneo(torneo.id, torneoData);
      if (error) throw error;
      await loadAll();
      setShowForm(false);
      await alert('Listo', 'Torneo actualizado.');
    } catch (err) {
      console.error(err);
      await alert('Error', 'No se pudo actualizar el torneo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTorneo = async () => {
    if (!canManageTorneos || !torneo) return;
    if (juegos.length > 0) {
      await alert(
        'No se puede eliminar',
        `Este torneo tiene ${juegos.length} partido(s) registrados. Elimínalos primero.`
      );
      return;
    }
    const confirmed = await confirm(
      'Eliminar torneo',
      `¿Eliminar "${fixMojibake(torneo.nombre)}"?`,
      { destructive: true }
    );
    if (!confirmed) return;
    try {
      const { error } = await deleteTorneo(torneo.id);
      if (error) throw error;
      await alert('Listo', 'Torneo eliminado.');
      navigate('/torneos');
    } catch (err) {
      console.error(err);
      await alert('Error', 'No se pudo eliminar el torneo.');
    }
  };

  const handleFinalizarTorneo = async () => {
    if (!canManageTorneos || !torneo) return;

    // Validaciones
    const warnings = [];
    const juegosSinResultado = juegos.filter((j) => {
      const estatus = (j.estatus || '').toLowerCase();
      return estatus !== 'terminado' && estatus !== 'cancelado';
    });
    const juegosSinMarcador = juegos.filter((j) => {
      const estatus = (j.estatus || '').toLowerCase();
      return estatus === 'terminado' && j.marcador_local == null && j.marcador_visitante == null;
    });

    if (juegosSinResultado.length > 0) {
      warnings.push(`${juegosSinResultado.length} juego(s) sin terminar o cancelar`);
    }
    if (juegosSinMarcador.length > 0) {
      warnings.push(`${juegosSinMarcador.length} juego(s) terminados sin marcador registrado`);
    }
    if (juegos.length === 0) {
      warnings.push('No hay juegos registrados en este torneo');
    }

    const warningText = warnings.length > 0
      ? `\n\nAdvertencias:\n• ${warnings.join('\n• ')}\n\n¿Finalizar de todos modos?`
      : '\n\n¿Confirmar?';

    if (!await confirm(
      'Finalizar torneo',
      `Finalizar "${fixMojibake(torneo.nombre)}"? El torneo se marcará como terminado.${warningText}`,
      { destructive: warnings.length > 0 }
    )) return;

    try {
      setSaving(true);
      const { error } = await updateTorneo(torneo.id, { estatus: 'finalizado' });
      if (error) throw error;
      await loadAll();
    } catch (err) {
      console.error(err);
      await alert('Error', 'No se pudo finalizar el torneo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDesinscribir = async (inscripcion) => {
    const juegosDeInscripcion = juegos.filter((j) => j.inscripcion_id === inscripcion.id || (j.torneo_id === torneo.id && j.grupo_id === inscripcion.grupo_id));
    if (juegosDeInscripcion.length > 0) {
      await alert(
        'No se puede desinscribir',
        `Este grupo tiene ${juegosDeInscripcion.length} partido(s). Eliminalos primero.`
      );
      return;
    }
    const confirmed = await confirm(
      'Desinscribir grupo',
      `¿Quitar a ${fixMojibake(inscripcion.grupos?.nombre_del_grupo || 'este grupo')} del torneo?`,
      { destructive: true }
    );
    if (!confirmed) return;
    try {
      const { error } = await deleteInscripcion(inscripcion.id);
      if (error) throw error;
      await loadAll();
      await alert('Listo', 'Grupo desinscrito.');
    } catch (err) {
      console.error(err);
      await alert('Error', 'No se pudo desinscribir el grupo.');
    }
  };

  if (loading) {
    return (
      <FeaturedLayout title="Torneo"
      refreshAction={loadAll}>
        <div className="py-20 text-center text-muted-foreground">Cargando...</div>
      </FeaturedLayout>
    );
  }

  // Llegar aqui con un torneo ajeno solo pasa por un enlace viejo. Se manda a su
  // pantalla en vez de mostrar una version apagada de esta. Directo a la ruta nueva:
  // antes iba a /torneos/externos/:id, que a su vez redirigia (dos saltos).
  if (!loading && torneo && esAjeno) {
    return <Navigate to={`/torneos-myklubb/${torneo.id}`} replace />;
  }

  if (error || !torneo) {
    return (
      <FeaturedLayout title="Torneo">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error || 'Torneo no encontrado.'}</AlertDescription>
        </Alert>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/torneos')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver
        </Button>
      </FeaturedLayout>
    );
  }

  return (
    <FeaturedLayout title={fixMojibake(torneo.nombre)}>
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/torneos')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Torneos
        </Button>

        <Card className="border shadow-sm">
          <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Trophy className="h-5 w-5 text-amber-600 shrink-0" />
                <CardTitle className="text-2xl">{fixMojibake(torneo.nombre)}</CardTitle>
                <Badge variant="outline" className={cn('text-xs', ETAPA_STYLES[etapaKey])}>
                  {statusLabel}
                </Badge>
              </div>
              {torneo.descripcion && (
                <CardDescription>{fixMojibake(torneo.descripcion)}</CardDescription>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span><CalendarIcon className="h-3 w-3 inline mr-1" />{formatDateRange(torneo.fecha_inicio, torneo.fecha_fin)}</span>
                <span>Formato: {FORMATO_LABELS[torneo.formato] || 'Liga'}</span>
                {torneo.sede && <span>Sede: {fixMojibake(torneo.sede)}</span>}
                {torneo.ligas?.nombre && <span>Liga: {fixMojibake(torneo.ligas.nombre)}</span>}
              </div>
              {/* El proximo juego, en el encabezado. Es lo que un coach viene a ver, y
                  hasta ahora habia que entrar a Calendario a buscarlo. */}
              {proximo && (
                <p className="mt-2 text-sm">
                  <span className="text-muted-foreground">Próximo: </span>
                  <span className="font-medium">
                    {fixMojibake(proximo.grupo?.nombre_del_grupo || 'Grupo')} vs {fixMojibake(proximo.equipo?.nombre || proximo.equipo_nombre || 'Por definir')}
                  </span>
                  <span className="text-muted-foreground">
                    {' · '}{format(parseLocalDate(proximo.fecha) || new Date(), "EEE d 'de' MMM", { locale: es })}
                    {proximo.hora ? ` · ${proximo.hora.slice(0, 5)}` : ''}
                    {proximo.ubicacion ? ` · ${fixMojibake(proximo.ubicacion)}` : ''}
                  </span>
                </p>
              )}
            </div>

            {canManageTorneos && (
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={() => setShowInscribir(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Inscribir grupo
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" title="Más acciones">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShowForm(true)}>
                      <Edit className="h-4 w-4" />
                      Editar torneo
                    </DropdownMenuItem>
                    {!torneoCerrado && (
                      <DropdownMenuItem onClick={handleFinalizarTorneo} disabled={saving}>
                        <Trophy className="h-4 w-4" />
                        Finalizar torneo
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleDeleteTorneo}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                      Eliminar torneo
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </CardHeader>
        </Card>

        <div className="rounded-xl border bg-muted/30 p-2 flex flex-wrap gap-2">
          {TAB_CONFIG.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition',
                  active ? 'bg-background shadow border border-border' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'inscripciones' && (
          <InscripcionesTab
            inscripciones={inscripciones}
            torneo={torneo}
            canManage={canManageTorneos}
            onInscribir={() => setShowInscribir(true)}
            onOpen={(insc) => navigate(`/torneos/${torneo.id}/inscripciones/${insc.id}`)}
            onDelete={handleDesinscribir}
          />
        )}

        {activeTab === 'calendario' && (
          <CalendarioTab
            jornadas={jornadas}
            jornadaIdx={jornadaIdx}
            onJornadaChange={setJornadaKey}
            navigate={navigate}
            canManage={canManageTorneos}
            torneoId={torneo.id}
            onCompartir={(fecha, partidos) => setRolACompartir({ fecha, partidos })}
            org={org}
          />
        )}

        {activeTab === 'posiciones' && (
          <RecordTab filas={record} />
        )}

        {activeTab === 'estadisticas' && (
          <EstadisticasTab
            torneo={torneo}
            inscripciones={inscripciones}
            grupoId={grupoEstadisticas}
            onGrupoChange={setGrupoEstadisticas}
          />
        )}
      </div>

      <FormTorneo
        open={showForm}
        onOpenChange={(v) => { if (!v) setShowForm(false); }}
        torneo={torneo}
        onSubmit={handleSubmitTorneo}
        onCancel={() => setShowForm(false)}
        saving={saving}
      />

      {showInscribir && (
        <InscribirGrupoModal
          torneo={torneo}
          gruposYaInscritos={inscripciones}
          onClose={() => setShowInscribir(false)}
          onSuccess={async () => {
            await loadAll();
          }}
        />
      )}

      {/* El flyer se arma con el adaptador: `juegos` esta escrita desde el club
          (puntos_favor/puntos_contra) y no guarda local/visitante, asi que el grupo propio
          va siempre a la izquierda. Ver src/lib/rolInterno.js. */}
      <CompartirRolModal
        open={Boolean(rolACompartir)}
        onOpenChange={(abierto) => { if (!abierto) setRolACompartir(null); }}
        titulo={tituloDelRolInterno(rolACompartir?.partidos)}
        fondoUrl={torneo?.flyer_bg_url || null}
        {...(rolACompartir ? armarRolInterno({ torneo, org, fecha: rolACompartir.fecha, juegos: rolACompartir.partidos }) : { rol: null, clubes: {} })}
      />

      <ConfirmDialog />
    </FeaturedLayout>
  );
};

const InscripcionesTab = ({ inscripciones, torneo, canManage, onInscribir, onOpen, onDelete }) => {
  if (inscripciones.length === 0) {
    return (
      <Card className="border shadow-sm">
        <CardContent className="py-12 text-center space-y-3">
          <Users className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Aún no hay grupos inscritos en este torneo.</p>
          {canManage && (
            <Button onClick={onInscribir}>
              <UserPlus className="h-4 w-4 mr-2" /> Inscribir primer grupo
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {inscripciones.map((insc) => {
        const g = insc.grupos || {};
        return (
          <Card key={insc.id} className="border shadow-sm hover:shadow-md transition cursor-pointer" onClick={() => onOpen(insc)}>
            <CardContent className="py-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate">{fixMojibake(g.nombre_del_grupo || 'Grupo')}</p>
                    {insc.estatus && (
                      <Badge
                        variant="outline"
                        className={cn('text-[10px]', INSCRIPCION_STATUS_STYLES[insc.estatus])}
                      >
                        {INSCRIPCION_STATUS_LABELS[insc.estatus] || insc.estatus}
                      </Badge>
                    )}
                    {insc.estatus === 'aceptada' && insc.roster_estado && (
                      <Badge variant="outline" className={cn('text-[10px]',
                        insc.roster_estado === 'cerrado'
                          ? 'border-slate-500/30 bg-slate-500/10 text-slate-600'
                          : 'border-sky-500/30 bg-sky-500/10 text-sky-600')}>
                        {insc.roster_estado === 'cerrado' ? 'Roster cerrado' : 'Roster abierto'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    {(g.categoria_base || insc.categorias?.nombre) && (
                      <Badge variant="secondary" className="text-[10px]">
                        {fixMojibake(g.categoria_base || insc.categorias?.nombre)}
                      </Badge>
                    )}
                    {g.rama && <Badge variant="outline" className="text-[10px]">{fixMojibake(g.rama)}</Badge>}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>

              <div className="grid grid-cols-4 gap-2 text-center text-xs border-y py-2">
                <Stat label="PJ" value={insc.partidos_jugados || 0} />
                <Stat label="G" value={insc.ganados || 0} />
                <Stat label="E" value={insc.empatados || 0} />
                <Stat label="P" value={insc.perdidos || 0} />
              </div>

              <div className="text-xs text-muted-foreground">
                PF {insc.puntos_favor || 0} / PC {insc.puntos_contra || 0}
              </div>

              {canManage && (
                <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600"
                    onClick={() => onDelete(insc)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <p className="font-bold">{value}</p>
    <p className="text-[10px] text-muted-foreground">{label}</p>
  </div>
);

// Un dia se puede compartir cuando alguno de sus partidos tiene HORA: el flyer es una
// rejilla de horas por campos y descarta los que no la traen. Sin esta condicion el boton
// abriria un rol en blanco.
const diaCompartible = (fecha, partidos) =>
  fecha !== 'Sin fecha' && (partidos || []).some((p) => p.hora);

const CalendarioTab = ({ jornadas, jornadaIdx, onJornadaChange, navigate, canManage, torneoId, onCompartir, org }) => {
  if (jornadas.length === 0) {
    return (
      <Card className="border shadow-sm">
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          <p className="mb-3">Aún no hay partidos programados en este torneo.</p>
          {canManage && (
            <Button size="sm" onClick={() => navigate('/torneos/juegos', { state: { torneoId } })}>
              <Plus className="h-4 w-4 mr-1" /> Crear primer juego
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }
  const idx = Math.min(Math.max(jornadaIdx, 0), jornadas.length - 1);
  const jornada = jornadas[idx];
  const bloques = bloquesPorHora(jornada.juegos);
  const ir = (paso) => {
    const destino = jornadas[idx + paso];
    if (destino) onJornadaChange(destino.key);
  };
  const compartible = diaCompartible(jornada.fecha || 'Sin fecha', jornada.juegos);

  return (
    <div className="space-y-3">
      {/* Una jornada a la vez: la de hoy (o la proxima) al abrir, flechas para moverse y
          el selector para saltar. "Compartir rol" comparte la que se esta viendo. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-9 w-9 shrink-0 p-0"
          disabled={idx === 0}
          onClick={() => ir(-1)}
          title="Jornada anterior"
          aria-label="Jornada anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Select value={jornada.key} onValueChange={onJornadaChange}>
          <SelectTrigger className="h-9 w-auto min-w-[240px] flex-1 sm:flex-none" aria-label="Jornada">
            <SelectValue>{jornada.titulo}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {jornadas.map((j) => (
              <SelectItem key={j.key} value={j.key}>{j.titulo}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          className="h-9 w-9 shrink-0 p-0"
          disabled={idx >= jornadas.length - 1}
          onClick={() => ir(1)}
          title="Jornada siguiente"
          aria-label="Jornada siguiente"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <span className="text-xs text-muted-foreground">
          {idx + 1} de {jornadas.length} · {jornada.juegos.length} {jornada.juegos.length === 1 ? 'juego' : 'juegos'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {compartible && (
            <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => onCompartir(jornada.fecha, jornada.juegos)}>
              <Share2 className="h-3.5 w-3.5 mr-1.5" /> Compartir rol
            </Button>
          )}
          {canManage && (
            <Button size="sm" className="h-9 text-xs" onClick={() => navigate('/torneos/juegos', { state: { torneoId } })}>
              <Plus className="h-4 w-4 mr-1" /> Crear juego
            </Button>
          )}
        </div>
      </div>

      {/* Un bloque por hora, con la hora en una COLUMNA a la izquierda y los juegos de
          esa hora a su derecha. La hora tenia su propio renglon y la fecha se repetia en
          el encabezado de la tarjeta -- tres titulos apilados para un juego, y eso se
          multiplica por cada horario (el dueno, 2026-09-09). La fecha vive en el selector,
          que esta justo arriba; el conteo, junto al de jornadas. */}
      <Card className="border shadow-sm">
        <CardContent className="divide-y p-0">
          {bloques.map((b) => (
            <div key={b.hora || 'sin-hora'} className="flex gap-3 px-3 py-1.5">
              <div className="flex w-16 shrink-0 items-center gap-1 pt-1.5 sm:w-20">
                <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="text-sm font-semibold tabular-nums">{b.hora || 'S/H'}</span>
              </div>
              {/* El mismo grupo puede estar en dos bloques: juega dos veces ese dia. */}
              <div className="min-w-0 flex-1 divide-y">
                {b.juegos.map((p) => (
                  <JuegoFila
                    key={p.id}
                    juego={p}
                    onView={() => navigate(`/torneos/juegos/${p.id}`)}
                    onEdit={canManage && puedeEditarJuego(p, org?.id)
                      ? () => navigate(`/torneos/juegos/${p.id}`, { state: { focus: 'editar' } })
                      : null}
                    onTakeAttendance={() => {
                      const gId = p.grupo_id || p.grupo?.id || p.grupos?.id;
                      if (gId) navigate(`/attendance/juego/${gId}/${p.id}`);
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// El record de los grupos del club en este torneo. NO es una tabla de posiciones: en el
// torneo interno el club captura solo sus juegos, los partidos entre rivales no existen
// aqui y nadie puede armar la tabla de la liga. Se llamaba "Posiciones" para decir lo
// mismo que el organizador y el micrositio -- pero esos si tienen la tabla completa; esta
// pantalla no, y el nombre prometia lo que no hay. Sin "#" ni PTS: los grupos del club
// juegan en categorias distintas y clasificarlos entre si era inventar.
const RecordTab = ({ filas }) => {
  const aviso = (
    <p className="text-xs text-muted-foreground">
      El récord de los grupos del club en este torneo, con los marcadores que capturaste. No es
      la tabla de la liga: los partidos entre los demás equipos no se capturan aquí.
    </p>
  );
  if (!hayRecord(filas)) {
    return (
      <Card className="border shadow-sm">
        <CardContent className="py-12 text-center text-sm text-muted-foreground space-y-2">
          <p>Aún no hay juegos terminados. El récord aparece cuando captures marcadores.</p>
          {aviso}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border shadow-sm">
      <CardContent className="py-4 space-y-3">
        {aviso}
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-2">Grupo</th>
              <th className="text-center py-2 px-2">JJ</th>
              <th className="text-center py-2 px-2">JG</th>
              <th className="text-center py-2 px-2">JE</th>
              <th className="text-center py-2 px-2">JP</th>
              <th className="text-center py-2 px-2">PF</th>
              <th className="text-center py-2 px-2">PC</th>
              <th className="text-center py-2 px-2">DIF</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((s) => (
              <tr key={s.id} className="border-b hover:bg-muted/30">
                <td className="py-2 px-2 font-medium">{s.nombre}</td>
                <td className="text-center">{s.partidos_jugados || 0}</td>
                <td className="text-center">{s.ganados || 0}</td>
                <td className="text-center">{s.empatados || 0}</td>
                <td className="text-center">{s.perdidos || 0}</td>
                <td className="text-center">{s.puntos_favor || 0}</td>
                <td className="text-center">{s.puntos_contra || 0}</td>
                <td className="text-center">{s.diferencia > 0 ? '+' : ''}{s.diferencia}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Las estadisticas del torneo.
//
// Antes eran cuatro numeros planos (inscripciones, partidos, jugados, puntos) mientras
// EstadisticasTorneo -- con % de victorias, promedios y la grafica de G/E/P -- vivia
// apagada detras de una ruta a la que nada navegaba. Aqui se enciende, con el filtro
// por grupo que le hacia falta: un torneo tiene varios grupos del club inscritos y el
// promedio de todos juntos no le dice nada a nadie.
const EstadisticasTab = ({ torneo, inscripciones, grupoId, onGrupoChange }) => {
  const grupos = useMemo(() => {
    const m = new Map();
    (inscripciones || []).forEach((i) => {
      if (i.grupo_id && !m.has(i.grupo_id)) {
        m.set(i.grupo_id, fixMojibake(i.grupos?.nombre_del_grupo || 'Grupo'));
      }
    });
    return [...m].map(([id, nombre]) => ({ id, nombre }));
  }, [inscripciones]);

  return (
    <div className="space-y-4">
      {grupos.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          <Button
            variant={grupoId === 'all' ? 'default' : 'outline'}
            size="sm"
            className="h-8 rounded-full px-3 text-xs font-normal"
            onClick={() => onGrupoChange('all')}
          >
            Todos
          </Button>
          {grupos.map((g) => (
            <Button
              key={g.id}
              variant={grupoId === g.id ? 'default' : 'outline'}
              size="sm"
              className="h-8 rounded-full px-3 text-xs font-normal"
              onClick={() => onGrupoChange(g.id)}
            >
              {g.nombre}
            </Button>
          ))}
        </div>
      )}
      <EstadisticasTorneo torneo={torneo} groupId={grupoId === 'all' ? null : grupoId} />
    </div>
  );
};


export default TorneoDetail;
